import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useEditorState } from '@tiptap/react';
import { ColumnsIcon, RowsIcon, Trash2Icon, TrashIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import type { DropdownOption } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { DeleteButtonProps, DeleteTableElementType } from '.';

export const deleteColRowOptions: DropdownOption<DeleteTableElementType>[] = [
  {
    label: 'Usuń kolumnę',
    type: 'deleteColumn',
    icon: ColumnsIcon,
  },
  {
    label: 'Usuń wiersz',
    type: 'deleteRow',
    icon: RowsIcon,
  },
  {
    label: 'Usuń tabelę',
    type: 'deleteTable',
    icon: Trash2Icon,
  },
];

function clickDeleteColRowItem(editor: Editor | null, type: DeleteTableElementType): void {
  if (!editor) return;
  switch (type) {
    case 'deleteColumn':
      editor.chain().focus().deleteColumn().run();
      break;
    case 'deleteRow':
      editor.chain().focus().deleteRow().run();
      break;
    case 'deleteTable':
      editor.chain().focus().deleteTable().run();
      break;
  }
}

function useIsActiveElement(editor: Editor | null, type: DeleteTableElementType): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return ctx.editor?.isActive(type) ?? false;
      },
    }) ?? false
  );
}

function getOption(type: DeleteTableElementType): DropdownOption<DeleteTableElementType> | undefined {
  return deleteColRowOptions.find((option) => option.type === type);
}

function useDeleteColRowState(editor: Editor | null, type: DeleteTableElementType) {
  const isActive: boolean = useIsActiveElement(editor, type);
  const option = useMemo(() => getOption(type), [type]);

  return {
    isActive,
    option,
  };
}

function useHandleClick(onClick: DeleteButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function DeleteColRowButton(deleteButtonProps: DeleteButtonProps) {
  const { editor, type, className, onClick, ...props } = deleteButtonProps;

  const { isActive, option } = useDeleteColRowState(editor, type);
  const inSchema: boolean = isNodeInSchema('table', editor);
  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  const Icon = option?.icon || TrashIcon;

  const handleClick = useHandleClick(onClick, () => clickDeleteColRowItem(editor, type));

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      aria-label={option?.label || type}
      aria-pressed={isActive}
      onClick={handleClick}
      className={cn('group aria-pressed:text-text-primary aria-pressed:bg-fill-secondary/60 aria-pressed:hover:bg-fill-secondary/80', className)}
      {...props}
    >
      <Icon className="group:aria-pressed:text-text-primary" />
      {option?.label}
    </DropdownMenuItem>
  );
}
